import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Play, Pause, Radio, Lock, Clock } from "lucide-react";
import { usePlayer } from "@/context/PlayerContext";
import { useAuth } from "@/context/AuthContext";
import { endpoints } from "@/lib/api";
import { AdSlot } from "@/components/AdSlot";
import { MembersBadge } from "@/components/MembersBadge";
import { RadioListeners } from "@/components/RadioListeners";
import { HighlightsBar } from "@/components/HighlightsBar";

export default function RadioPage() {
  const { nowPlaying, isPlaying, requestPlay } = usePlayer();
  const { isSubscribed, isAuthed } = useAuth();
  const [schedule, setSchedule] = useState([]);

  useEffect(() => { endpoints.schedule().then((r) => setSchedule(r.data)).catch(() => {}); }, []);

  const onPlay = () => requestPlay();

  return (
    <div className="px-4 sm:px-6 lg:px-8 max-w-7xl mx-auto py-10">
      <MembersBadge className="mb-6" />
      <AdSlot placement="radio" position="top" className="mb-6" />
      <div className="grid lg:grid-cols-3 gap-8">
        <div className="lg:col-span-2 min-w-0">
          <HighlightsBar className="mb-4" />
          <div className="relative rounded-2xl overflow-hidden border border-[#273042] blue-glow">
            <img src={nowPlaying?.coverImage || "/img/radio.jpg"} alt="B&B KIGALI live radio studio" className="w-full aspect-[4/5] sm:aspect-video object-cover" />
            <div className="absolute inset-0 bg-gradient-to-t from-black/90 via-black/40 to-black/20" />
            <div className="absolute inset-0 flex flex-col items-center justify-center text-center p-6">
              <div className="text-xs uppercase tracking-wider text-[#E11D2A] font-bold flex items-center gap-1.5 mb-3"><span className="w-1.5 h-1.5 rounded-full bg-[#EF4444] live-dot" /> Live · 89.7 FM</div>
              <h1 className="text-3xl sm:text-4xl font-extrabold text-white">{nowPlaying?.showTitle || "B&B KIGALI Live"}</h1>
              <p className="text-slate-300 mt-1">{nowPlaying?.djName || "24/7 Radio"}</p>
              <RadioListeners className="mt-3 px-3 py-1.5 rounded-full bg-black/50 border border-white/10 text-white" testid="radio-page-listeners" />
              <button onClick={onPlay} data-testid="radio-page-play-btn" aria-label="Play or pause live radio"
                className="mt-7 w-16 h-16 rounded-full bg-[#E11D2A] hover:bg-[#C8121F] text-white flex items-center justify-center btn-glow transition-colors">
                {isPlaying ? <Pause className="w-7 h-7" /> : <Play className="w-7 h-7 ml-1" />}
              </button>
              {!isSubscribed && (
                <button onClick={() => { if (!isAuthed) window.dispatchEvent(new Event("open-auth")); else window.dispatchEvent(new CustomEvent("open-subscribe")); }} data-testid="radio-unlock-btn"
                  className="mt-5 inline-flex items-center gap-1.5 h-11 px-6 rounded-full bg-[#E11D2A] hover:bg-[#C8121F] text-white text-sm font-extrabold uppercase btn-glow"><Lock className="w-4 h-4" /> Unlock live radio</button>
              )}
            </div>
          </div>
          <div className="mt-6 rounded-xl bg-[#121620] border border-[#273042] p-6">
            <h2 className="text-white font-bold flex items-center gap-2"><Radio className="w-5 h-5 text-[#E11D2A]" /> About the station</h2>
            <p className="text-slate-400 text-sm mt-2 leading-relaxed">B&B KIGALI 89.7 broadcasts music, news, culture and talk 24/7 from Kigali, Rwanda — reaching listeners at home and across the diaspora. Premium members enjoy ad-free, high-quality streaming.</p>
          </div>
        </div>

        <div>
          <h2 className="text-white font-bold mb-4 flex items-center gap-2"><Clock className="w-5 h-5 text-[#E11D2A]" /> Today's schedule</h2>
          <div className="space-y-2">
            {schedule.map((s) => (
              <div key={s.id} data-testid={`radio-schedule-${s.id}`} className={`rounded-xl border p-4 ${s.status === "on-air" ? "border-[#E11D2A] bg-[#E11D2A]/10" : "border-[#273042] bg-[#121620]"}`}>
                <div className="flex items-center justify-between">
                  <span className="font-mono text-xs text-slate-400">{s.time}</span>
                  {s.status === "on-air" && <span className="text-[10px] uppercase font-bold text-[#E11D2A] flex items-center gap-1"><span className="w-1.5 h-1.5 rounded-full bg-[#EF4444] live-dot" /> On air</span>}
                </div>
                <div className="text-white font-semibold text-sm mt-1">{s.showTitle}</div>
                <div className="text-xs text-slate-400">{s.djName}</div>
              </div>
            ))}
          </div>
        </div>
      </div>
      <AdSlot placement="radio" position="bottom" className="mt-8" />
    </div>
  );
}
